/* ═══════════════════════════════════════════════════════════════
   ForecastingPage — week-based resource planning grid.

   Layout (industry-standard, modeled on Float / Forecast / Productive):
     ┌──────────────────────────────────────────────────────────┐
     │ Toolbar: week-pick, group-by, copy-prev, filters, views  │
     ├──────────────────────────────────────────────────────────┤
     │ Sticky day header (Mon Tue Wed Thu Fri Sat Sun + Total)  │
     │ Department / Project group ▼                             │
     │   Person row → 7 day cells → capacity bar at right       │
     │     Each cell = stack of colored project blocks          │
     │   ...                                                    │
     │ Group ▼ ...                                              │
     └──────────────────────────────────────────────────────────┘

   Behaviors:
     - Click cell → assign popover (always opens — read-only banner
       when caller lacks edit perm or row is vacation-source).
     - Vacation cells (sourceKind='vacation') render locked + grayed.
     - Capacity bar measures worked vs available (vacation-adjusted).
     - Daily over-allocation ringed in red on the day cell.
     - Saved views via DashboardViewService (surface = 'forecasting')
       with full CRUD: apply, save-as, update, rename, delete, default,
       share-toggle, drift indicator.
     - Atomic single-cell upsert via forecast_upsert_cell server action
       (backed by partial unique index — no dupes possible).

   Permissions:
     - read: forecasting.view_own (own row only) | view_team | view_all
     - write: forecasting.edit_own (own cells) | edit_team | edit_any
     - delete: forecasting.delete_team | delete_any
     - copy-prev-week / paste:  forecasting.bulk_assign

   Component split (forecasting/):
     - _shared.tsx  — types, styles, Modal, SegmentedControl, ToggleChip
     - Toolbar.tsx  — top bar
     - Grid.tsx     — DayHeaderCell, GroupHeader, MemberRow, DayCell, CapacityCell
     - CellEditor.tsx
     - FilterPanel.tsx
     - ViewsMenu.tsx
   ═══════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { TeamMember, Department, HourForecast, Project } from '../../shared/types';
import { TeamService } from '../services/teamService';
import { ProjectService } from '../services/projectService';
import { DepartmentService } from '../services/departmentService';
import { TimeService, mondayOf, addDays } from '../services/timeService';
import { DashboardViewService } from '../services/dashboardViewService';
import { hasPermission } from '../../shared/permissions';
import type { DashboardView, ProjectAssignment, Team, TeamAssignment } from '../../shared/entities';
import {
  type MemberCapacity,
  type Scope,
  type ViewConfig,
  DEFAULT_CONFIG,
  VACATION_COLOR,
  daysBetween,
  headerCell,
  projectColor,
  todayIso,
} from './forecasting/_shared';
import { Toolbar } from './forecasting/Toolbar';
import { CellEditor } from './forecasting/CellEditor';
import { DayHeaderCell, GroupHeader, MemberRow } from './forecasting/Grid';
import { ProjectView } from './forecasting/ProjectView';
import EmptyState from '../components/EmptyState';

interface ForecastingPageProps {
  currentUserId: string;
  currentUserPermissions: string[];
  /** Optional one-shot filter from a cross-page drill (e.g. dashboard's
   *  "Forecast next 4 weeks" bar click → land here filtered to that week
   *  + that project). Cleared via onInitialFilterConsumed after applying. */
  initialFilter?: { projectId?: string; weekStart?: string; memberId?: string } | null;
  onInitialFilterConsumed?: () => void;
}

export default function ForecastingPage({
  currentUserId,
  currentUserPermissions,
  initialFilter,
  onInitialFilterConsumed,
}: ForecastingPageProps) {
  // Permission gates
  const canViewAll = hasPermission(currentUserPermissions, 'forecasting.view_all');
  const canViewTeam = hasPermission(currentUserPermissions, 'forecasting.view_team');
  const canEditAny = hasPermission(currentUserPermissions, 'forecasting.edit_any');
  const canEditTeam = hasPermission(currentUserPermissions, 'forecasting.edit_team');
  const canBulk = hasPermission(currentUserPermissions, 'forecasting.bulk_assign');

  // Week selection — Monday-anchored. Persisted in localStorage so navigating
  // away from the page and back doesn't snap the user to a different week
  // (and convince them their saved forecasts vanished).
  const WEEK_STORAGE_KEY = 'forecast.weekStart';
  const [weekStart, setWeekStartRaw] = useState<string>(() => {
    try {
      const saved = window.localStorage.getItem(WEEK_STORAGE_KEY);
      // Sanity-check: must look like yyyy-mm-dd to avoid trusting bad input.
      if (saved && /^\d{4}-\d{2}-\d{2}$/.test(saved)) return saved;
    } catch {
      /* ignore storage failure (private mode etc.) */
    }
    return mondayOf(todayIso());
  });
  const setWeekStart = useCallback((next: string) => {
    setWeekStartRaw(next);
    try {
      window.localStorage.setItem(WEEK_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);
  const weekDays = useMemo(() => {
    const days: string[] = [];
    for (let i = 0; i < 7; i++) days.push(addDays(weekStart, i));
    return days;
  }, [weekStart]);

  // Data
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  const [projectAssignments, setProjectAssignments] = useState<ProjectAssignment[]>([]);
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);

  // View state
  const [config, setConfig] = useState<ViewConfig>(DEFAULT_CONFIG);
  const [savedViews, setSavedViews] = useState<DashboardView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);

  // Editor popover
  const [editingCell, setEditingCell] = useState<{
    memberId: string;
    dayDate: string;
    forecastId?: string;
  } | null>(null);

  // Collapsed groups (per-session, not persisted)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Global free-text search — projects, members, teams, departments. Kept
  // local to the page (not persisted in saved views) because a search is
  // a transient question ("what's happening with X?"), not a setting.
  const [searchQuery, setSearchQuery] = useState('');

  // One-shot deep-link consumer: a click on the project dashboard's
  // "Forecast Outlook" or "Burn Rate" widget can land here pre-filtered
  // to a project + week. We snap weekStart, scope to the project, and
  // notify the parent so the same link doesn't re-fire.
  useEffect(() => {
    if (!initialFilter) return;
    if (initialFilter.weekStart) setWeekStart(mondayOf(initialFilter.weekStart));
    if (initialFilter.projectId) {
      setConfig((c) => ({
        ...c,
        selectedProjectIds: [initialFilter.projectId!],
        // Force project-grouped layout so the filter is visually obvious.
        groupBy: 'project',
      }));
    }
    onInitialFilterConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  // Initial load. Each source is settled independently (Promise.allSettled)
  // so one bad endpoint (e.g. projects service down) doesn't blank the
  // whole page — every other successfully-loaded piece still renders, and
  // `loadError` surfaces an inline notice instead of failing silently.
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [memsR, deptsR, projsR, viewsListR, projAssignsR, teamAssignsR, teamListR] =
          await Promise.allSettled([
            TeamService.list(),
            DepartmentService.list().catch(() => [] as Department[]),
            ProjectService.list(),
            DashboardViewService.listForSurface('forecasting', currentUserId).catch(
              () => [] as DashboardView[]
            ),
            TeamService.listAssignments().catch(() => [] as ProjectAssignment[]),
            TeamService.listTeamAssignments().catch(() => [] as TeamAssignment[]),
            TeamService.listTeams().catch(() => [] as Team[]),
          ]);
        if (memsR.status === 'fulfilled') setMembers(memsR.value);
        if (deptsR.status === 'fulfilled') setDepartments(deptsR.value);
        if (projsR.status === 'fulfilled') setProjects(projsR.value);
        if (viewsListR.status === 'fulfilled') setSavedViews(viewsListR.value);
        if (projAssignsR.status === 'fulfilled') setProjectAssignments(projAssignsR.value);
        if (teamAssignsR.status === 'fulfilled') setTeamAssignments(teamAssignsR.value);
        if (teamListR.status === 'fulfilled') setTeams(teamListR.value);

        if (memsR.status === 'rejected' || projsR.status === 'rejected') {
          setLoadError(
            'Some forecasting data failed to load. Team members or projects may be incomplete.'
          );
        }

        // Apply user's default view if one exists.
        if (viewsListR.status === 'fulfilled') {
          const def = viewsListR.value.find((v) => v.userId === currentUserId && v.isDefault);
          if (def && def.config) {
            setConfig({ ...DEFAULT_CONFIG, ...(def.config as Partial<ViewConfig>) });
            setActiveViewId(def.id);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUserId]);

  // Reload forecasts when week changes. `forecastsLoading` is a separate
  // signal from the page-level `loading` (which guards first paint) so we
  // can show a subtle "refreshing" indicator on subsequent navigation
  // instead of either blanking the page or silently swapping data.
  const [forecastsLoading, setForecastsLoading] = useState(false);
  const loadForecasts = useCallback(async () => {
    const start = weekDays[0];
    const end = weekDays[6];
    setForecastsLoading(true);
    try {
      const data = await TimeService.listForecastsForRange(start, end);
      setForecasts(data);
    } finally {
      setForecastsLoading(false);
    }
  }, [weekDays]);
  useEffect(() => {
    loadForecasts();
  }, [loadForecasts]);
  useEffect(() => {
    const h = () => loadForecasts();
    window.addEventListener('specforma-sync-complete', h);
    return () => window.removeEventListener('specforma-sync-complete', h);
  }, [loadForecasts]);

  // ─── Scope sets ───────────────────────────────────────────────────
  // Compute, once per data load, the three scope sets the user could
  // pick from. The toolbar Scope segmented control just toggles which
  // set is shown.
  const myTeamMemberIds = useMemo<Set<string>>(() => {
    // Teams the user leads (team_assignments.teamRole === 'leader').
    const ledTeams = new Set(
      teamAssignments
        .filter((a) => a.teamMemberId === currentUserId && a.teamRole === 'leader')
        .map((a) => a.teamId)
    );
    if (ledTeams.size === 0) return new Set();
    // All members of those teams (excluding the lead themself? — include them
    // so a team lead viewing "Team" sees their own row too).
    const ids = new Set<string>();
    for (const a of teamAssignments) {
      if (ledTeams.has(a.teamId)) ids.add(a.teamMemberId);
    }
    ids.add(currentUserId);
    return ids;
  }, [teamAssignments, currentUserId]);

  const myPmProjectIds = useMemo<Set<string>>(
    () =>
      new Set(
        projects
          .filter((p) => p.pmId === currentUserId || p.picId === currentUserId)
          .map((p) => p.id)
      ),
    [projects, currentUserId]
  );

  const myPmMemberIds = useMemo<Set<string>>(() => {
    if (myPmProjectIds.size === 0) return new Set();
    const ids = new Set<string>([currentUserId]); // PM always sees self
    // Anyone with a project_assignment on a project I PM
    for (const a of projectAssignments) {
      if (myPmProjectIds.has(a.projectId)) ids.add(a.teamMemberId);
    }
    // Anyone with any forecast row this week on a project I PM
    for (const f of forecasts) {
      if (f.projectId && myPmProjectIds.has(f.projectId)) ids.add(f.teamMemberId);
    }
    return ids;
  }, [myPmProjectIds, projectAssignments, forecasts, currentUserId]);

  /** Available scope choices for the Scope segmented control. Hidden when
   *  the user has no data to back the choice (e.g. no led teams → no Team). */
  const scopeOptions = useMemo(
    () => [
      { value: 'mine' as Scope, label: 'Mine', available: true },
      { value: 'team' as Scope, label: 'My team', available: myTeamMemberIds.size > 0 },
      { value: 'pm' as Scope, label: 'My projects', available: myPmProjectIds.size > 0 },
      { value: 'all' as Scope, label: 'All', available: canViewAll },
    ],
    [myTeamMemberIds, myPmProjectIds, canViewAll]
  );

  /** Self-correcting effect: if the active scope is no longer available
   *  (e.g. role changed, data drift), fall back to the broadest option the
   *  user can still see. */
  useEffect(() => {
    const activeOpt = scopeOptions.find((o) => o.value === config.scope);
    if (!activeOpt || !activeOpt.available) {
      const fallback =
        scopeOptions.find((o) => o.value === 'all' && o.available)?.value ||
        scopeOptions.find((o) => o.value === 'pm' && o.available)?.value ||
        scopeOptions.find((o) => o.value === 'team' && o.available)?.value ||
        ('mine' as Scope);
      if (fallback !== config.scope) {
        setConfig((prev) => ({ ...prev, scope: fallback }));
      }
    }
    // Pick a smart default scope on first load — only when scope was never
    // touched (still the DEFAULT 'mine'). Order: all > pm > team > mine.
    // Saved views override this because they ship with their own scope.
  }, [scopeOptions, config.scope]);

  // ─── Filtering / grouping ─────────────────────────────────────────

  // `scopedMembers` = members narrowed by perm + scope + dept filter. The
  // search pass on top of this becomes `visibleMembers` below; downstream
  // code only ever reads `visibleMembers` so search "just works" everywhere.
  const scopedMembers = useMemo(() => {
    let m = members.filter((x) => x.active || config.showInactiveMembers);

    // Scope: which set of people. View-permission acts as a hard ceiling
    // (a user without view_all literally cannot see everyone).
    const allowedByPerm = (id: string): boolean => {
      if (canViewAll) return true;
      if (id === currentUserId) return true;
      if (canViewTeam && myTeamMemberIds.has(id)) return true;
      if (myPmMemberIds.has(id)) return true; // PM is implicit team-of-projects
      return false;
    };
    m = m.filter((x) => allowedByPerm(x.id));

    if (config.scope === 'mine') {
      m = m.filter((x) => x.id === currentUserId);
    } else if (config.scope === 'team') {
      m = m.filter((x) => myTeamMemberIds.has(x.id));
    } else if (config.scope === 'pm') {
      m = m.filter((x) => myPmMemberIds.has(x.id));
    }
    // 'all' = no further narrowing beyond the perm ceiling.

    if (config.selectedDepartmentIds.length > 0) {
      m = m.filter((x) => x.departmentId && config.selectedDepartmentIds.includes(x.departmentId));
    }
    if (config.selectedTeamIds.length > 0) {
      // Team filter: keep members who belong to at least one selected team.
      const wantedTeams = new Set(config.selectedTeamIds);
      const memberInWantedTeam = new Set<string>();
      for (const a of teamAssignments) {
        if (wantedTeams.has(a.teamId)) memberInWantedTeam.add(a.teamMemberId);
      }
      m = m.filter((x) => memberInWantedTeam.has(x.id));
    }
    return m;
  }, [
    members,
    config,
    canViewAll,
    canViewTeam,
    currentUserId,
    myTeamMemberIds,
    myPmMemberIds,
    teamAssignments,
  ]);

  // ─── Global search ────────────────────────────────────────────────
  // Free-text query applies as a final pass over visibleMembers and
  // visibleProjects. Match rules (case-insensitive substring):
  //   - member: name, dept name, any team name they're on
  //   - project: name, project number, OR any member on it matches
  //   - team / dept matches expand into the members under them
  // The result: typing "Smith" surfaces Jane Smith's row + every project
  // she's on. Typing "MEPT" surfaces all MEPT members + projects they're
  // booked on. Typing "Tower" surfaces just that project.
  const search = searchQuery.trim().toLowerCase();
  const searchMatchedMemberIds = useMemo<Set<string> | null>(() => {
    if (!search) return null;
    const matched = new Set<string>();
    const matchingTeamIds = new Set(
      teams.filter((t) => t.name.toLowerCase().includes(search)).map((t) => t.id)
    );
    const matchingDeptIds = new Set(
      departments
        .filter(
          (d) =>
            d.name.toLowerCase().includes(search) ||
            (d.abbreviation || '').toLowerCase().includes(search)
        )
        .map((d) => d.id)
    );
    for (const m of members) {
      if (m.name.toLowerCase().includes(search)) {
        matched.add(m.id);
        continue;
      }
      if (m.departmentId && matchingDeptIds.has(m.departmentId)) {
        matched.add(m.id);
        continue;
      }
      // Team match: walk team_assignments for this member.
      for (const a of teamAssignments) {
        if (a.teamMemberId === m.id && matchingTeamIds.has(a.teamId)) {
          matched.add(m.id);
          break;
        }
      }
    }
    return matched;
  }, [search, members, teams, departments, teamAssignments]);

  const searchMatchedProjectIds = useMemo<Set<string> | null>(() => {
    if (!search) return null;
    const matched = new Set<string>();
    for (const p of projects) {
      if (
        p.name.toLowerCase().includes(search) ||
        (p.projectNumber || '').toLowerCase().includes(search)
      ) {
        matched.add(p.id);
      }
    }
    // Also include any project where a matched member has forecasts. Lets
    // a search for "Smith" surface every project Smith is booked on, even
    // if the project name doesn't contain "Smith".
    if (searchMatchedMemberIds) {
      for (const f of forecasts) {
        if (!f.projectId) continue;
        if (searchMatchedMemberIds.has(f.teamMemberId)) {
          matched.add(f.projectId);
        }
      }
      // Plus PM/PIC links so a project shows up when its PM matches.
      for (const p of projects) {
        if (
          (p.pmId && searchMatchedMemberIds.has(p.pmId)) ||
          (p.picId && searchMatchedMemberIds.has(p.picId))
        ) {
          matched.add(p.id);
        }
      }
    }
    return matched;
  }, [search, projects, forecasts, searchMatchedMemberIds]);

  // `visibleMembers` = scopedMembers ∩ search results. This is what the
  // grid + ProjectView actually render.
  const visibleMembers = useMemo(() => {
    if (!searchMatchedMemberIds && !searchMatchedProjectIds) return scopedMembers;
    return scopedMembers.filter((m) => {
      if (searchMatchedMemberIds && searchMatchedMemberIds.has(m.id)) return true;
      if (searchMatchedProjectIds) {
        for (const f of forecasts) {
          if (f.teamMemberId === m.id && f.projectId && searchMatchedProjectIds.has(f.projectId))
            return true;
        }
        for (const a of projectAssignments) {
          if (a.teamMemberId === m.id && searchMatchedProjectIds.has(a.projectId)) return true;
        }
      }
      return false;
    });
  }, [
    scopedMembers,
    searchMatchedMemberIds,
    searchMatchedProjectIds,
    forecasts,
    projectAssignments,
  ]);

  const visibleProjects = useMemo(() => {
    let p =
      config.selectedProjectIds.length === 0
        ? projects
        : projects.filter((proj) => config.selectedProjectIds.includes(proj.id));
    if (searchMatchedProjectIds) {
      p = p.filter((proj) => searchMatchedProjectIds.has(proj.id));
    }
    return p;
  }, [projects, config.selectedProjectIds, searchMatchedProjectIds]);

  // Forecast index: { memberId → { dayDate → ForecastEntry[] } }
  const forecastIndex = useMemo(() => {
    const idx = new Map<string, Map<string, HourForecast[]>>();
    const projectIds = new Set(visibleProjects.map((p) => p.id));
    for (const f of forecasts) {
      // Skip forecasts for filtered-out projects (vacation rows pass through —
      // they have no projectId, so the filter doesn't exclude them).
      if (config.selectedProjectIds.length > 0 && f.projectId && !projectIds.has(f.projectId))
        continue;
      const day = f.dayDate || f.weekStartDate;
      if (!day) continue;
      let byDay = idx.get(f.teamMemberId);
      if (!byDay) {
        byDay = new Map();
        idx.set(f.teamMemberId, byDay);
      }
      const arr = byDay.get(day) || [];
      arr.push(f);
      byDay.set(day, arr);
    }
    return idx;
  }, [forecasts, visibleProjects, config.selectedProjectIds]);

  // Group rows by department / project / none.
  //   department: 1 group per dept, members ordered by name.
  //   project:    1 group per project that has any forecast in the week,
  //               members appear under every project they're booked on.
  //   none:       single flat list.
  type GroupRow = { id: string; label: string; color?: string; members: TeamMember[] };
  const groupedRows = useMemo<GroupRow[]>(() => {
    if (config.groupBy === 'department') {
      const byDept = new Map<string, TeamMember[]>();
      const noDept: TeamMember[] = [];
      for (const m of visibleMembers) {
        if (m.departmentId) {
          const arr = byDept.get(m.departmentId) || [];
          arr.push(m);
          byDept.set(m.departmentId, arr);
        } else {
          noDept.push(m);
        }
      }
      const ordered: GroupRow[] = [];
      for (const d of departments) {
        const ms = byDept.get(d.id);
        if (ms && ms.length) ordered.push({ id: d.id, label: d.name, color: d.color, members: ms });
      }
      if (noDept.length) ordered.push({ id: '__none', label: 'Unassigned', members: noDept });
      return ordered;
    }
    if (config.groupBy === 'team') {
      // Members can be on multiple teams — they appear under each team
      // they belong to (matches the team-pivot mental model: "show me my
      // Gold team, members appear once per team they're a member of").
      const byTeam = new Map<string, TeamMember[]>();
      const memberToTeams = new Map<string, string[]>();
      for (const a of teamAssignments) {
        const arr = memberToTeams.get(a.teamMemberId) || [];
        arr.push(a.teamId);
        memberToTeams.set(a.teamMemberId, arr);
      }
      const noTeam: TeamMember[] = [];
      for (const m of visibleMembers) {
        const ts = memberToTeams.get(m.id);
        if (!ts || ts.length === 0) {
          noTeam.push(m);
          continue;
        }
        for (const tid of ts) {
          const arr = byTeam.get(tid) || [];
          arr.push(m);
          byTeam.set(tid, arr);
        }
      }
      const ordered: GroupRow[] = [];
      for (const t of teams) {
        const ms = byTeam.get(t.id);
        if (ms && ms.length) {
          ordered.push({
            id: `team:${t.id}`,
            label: t.name,
            color: t.color,
            members: ms.slice().sort((a, b) => a.name.localeCompare(b.name)),
          });
        }
      }
      if (noTeam.length) {
        ordered.push({ id: '__noteam', label: 'No team', members: noTeam });
      }
      return ordered;
    }
    if (config.groupBy === 'project') {
      // Project view is rendered by <ProjectView /> with its own aggregation
      // (per-unit breakdown sub-rows). Short-circuit here so we don't pay
      // for two passes of the same loop on every config change.
      return [];
    }
    return [{ id: '__all', label: '', members: visibleMembers }];
  }, [visibleMembers, departments, teams, teamAssignments, config.groupBy]);

  // ─── Capacity calc ────────────────────────────────────────────────
  // Memoize per-member computation once per (forecastIndex, week, members)
  // change. Avoids 50× recompute on every parent re-render.
  const capacityByMember = useMemo(() => {
    const out = new Map<string, MemberCapacity>();
    for (const m of members) {
      const isPartTime = m.employmentType === 'part_time_hourly';
      const rawTarget = m.weeklyCapacityHours ?? (isPartTime ? 20 : 40);
      const byDay = forecastIndex.get(m.id);
      let totalHours = 0;
      let vacationHours = 0;
      if (byDay) {
        for (const day of weekDays) {
          for (const f of byDay.get(day) || []) {
            const h = Number(f.hours) || 0;
            totalHours += h;
            if (f.sourceKind === 'vacation') vacationHours += h;
          }
        }
      }
      const workedHours = totalHours - vacationHours;
      const availableHours = Math.max(0, rawTarget - vacationHours);

      // Color / label rules:
      //   • Entire week vacation → neutral, "On vacation".
      //   • Otherwise compare workedHours against availableHours so
      //     vacation reduces both sides and the percentage means
      //     "how full is the work-time you actually had this week".
      let color = 'var(--ok)';
      let label = 'On target';
      if (availableHours === 0 && vacationHours > 0) {
        color = VACATION_COLOR;
        label = 'On vacation';
      } else if (isPartTime) {
        if (workedHours > availableHours * 1.05) {
          color = 'var(--dg)';
          label = 'Over-allocated';
        } else if (workedHours > availableHours * 0.9) {
          color = 'var(--ac)';
          label = 'Near limit';
        } else {
          color = 'var(--tm)';
          label = 'Light';
        }
      } else {
        if (workedHours < availableHours * 0.85) {
          color = 'var(--dg)';
          label = 'Under-allocated';
        } else if (workedHours < availableHours * 0.95) {
          color = 'var(--ac)';
          label = 'Light';
        } else if (workedHours > availableHours * 1.1) {
          color = 'var(--color-caution)';
          label = 'Stretched';
        } else {
          color = 'var(--ok)';
          label = 'On target';
        }
      }

      out.set(m.id, {
        rawTarget,
        vacationHours,
        workedHours,
        availableHours,
        totalHours,
        isPartTime,
        color,
        label,
      });
    }
    return out;
  }, [members, weekDays, forecastIndex]);

  // Per-day capacity target — used by DayCell to flag over-allocation on
  // a single day (e.g. 12h booked Mon when daily cap is 8h).
  function dailyTargetFor(member: TeamMember): number {
    const cap = capacityByMember.get(member.id);
    const weeklyAvail = cap?.availableHours ?? cap?.rawTarget ?? 8;
    const workdays = config.showWeekend ? 7 : 5;
    return weeklyAvail > 0 ? weeklyAvail / workdays : 8;
  }

  // ─── Cell editing ─────────────────────────────────────────────────

  function canEditCell(memberId: string): boolean {
    if (canEditAny) return true;
    // Own row is always editable — server still enforces forecasting.edit_own
    // on the upsert action so this is a pure UX affordance, not a security
    // boundary.
    if (currentUserId && memberId === currentUserId) return true;
    if (canEditTeam) {
      // Conservative: same-department check until project-membership
      // server enforcement lands.
      const me = members.find((m) => m.id === currentUserId);
      const target = members.find((m) => m.id === memberId);
      if (me?.departmentId && target?.departmentId === me.departmentId) return true;
    }
    return false;
  }

  // ─── Copy previous week (bulk_assign) ─────────────────────────────
  // Pulls every manual cell from the prior 7 days and writes the same
  // shape (member × project × phase × day) into the current week. Uses
  // the atomic upsert so existing cells overwrite cleanly. Toolbar gates
  // visibility on `canBulk`.
  const [copying, setCopying] = useState(false);
  async function copyPreviousWeek() {
    if (copying) return;
    const prevStart = addDays(weekStart, -7);
    const prevEnd = addDays(prevStart, 6);
    if (
      !window.confirm(
        `Copy every manual forecast from ${prevStart} – ${prevEnd} into ${weekDays[0]} – ${weekDays[6]}?\n\n` +
          `Existing cells in the destination week will be overwritten.`
      )
    ) {
      return;
    }
    setCopying(true);
    try {
      const visibleIds = visibleMembers.map((m) => m.id);
      const src = await TimeService.listForecastsForRange(prevStart, prevEnd, visibleIds);
      const manual = src.filter((f) => f.sourceKind !== 'vacation' && f.projectId);
      const queue = [...manual];
      async function worker() {
        while (queue.length) {
          const f = queue.shift();
          if (!f) return;
          const targetDay = addDays(weekStart, daysBetween(prevStart, f.dayDate || prevStart));
          if (!targetDay || targetDay < weekDays[0] || targetDay > weekDays[6]) continue;
          // phaseId omitted — server-side trigger stamps it from the
          // project's current_phase_id (migration 017).
          await TimeService.setForecastDayCell({
            teamMemberId: f.teamMemberId,
            projectId: f.projectId,
            dayDate: targetDay,
            hours: Number(f.hours) || 0,
            notes: f.notes,
          });
        }
      }
      // Cap concurrency at 8 so a 50-person firm × 30 cells per person
      // doesn't blow up the EC2 worker pool.
      await Promise.all(Array.from({ length: 8 }, () => worker()));
      await loadForecasts();
    } catch (err) {
      console.error('[forecast] copyPreviousWeek failed', err);
      window.alert(
        'Copy failed: ' +
          (err instanceof Error ? err.message : 'unknown error') +
          '\nPartial copy may have written some cells; check the grid before retrying.'
      );
    } finally {
      setCopying(false);
    }
  }

  // ─── Saved-view actions ───────────────────────────────────────────
  // Drift detection: deep-compare current `config` against the active
  // view's saved config so the toolbar can show a "(modified)" hint.
  const activeView = savedViews.find((v) => v.id === activeViewId) || null;
  const viewIsDirty = useMemo(() => {
    if (!activeView) return false;
    const saved = activeView.config as Partial<ViewConfig> | undefined;
    if (!saved) return true;
    const merged = { ...DEFAULT_CONFIG, ...saved };
    return JSON.stringify(merged) !== JSON.stringify(config);
  }, [activeView, config]);

  async function refreshViews() {
    const list = await DashboardViewService.listForSurface('forecasting', currentUserId);
    setSavedViews(list);
  }

  async function saveCurrentAsView(name: string) {
    const view: DashboardView = {
      id: '',
      userId: currentUserId,
      surface: 'forecasting',
      name,
      config: { ...config },
      isDefault: false,
      shared: false,
    };
    const saved = await DashboardViewService.save(view);
    await refreshViews();
    setActiveViewId(saved.id);
  }

  async function updateActiveView() {
    if (!activeView) return;
    await DashboardViewService.save({ ...activeView, config: { ...config } });
    await refreshViews();
  }

  async function deleteView(id: string) {
    if (!window.confirm('Delete this saved view? This cannot be undone.')) return;
    await DashboardViewService.delete(id);
    if (activeViewId === id) setActiveViewId(null);
    await refreshViews();
  }

  async function setDefaultView(id: string) {
    await DashboardViewService.setDefault(id, 'forecasting', currentUserId);
    await refreshViews();
  }

  async function renameView(id: string, name: string) {
    const v = savedViews.find((x) => x.id === id);
    if (!v) return;
    await DashboardViewService.save({ ...v, name });
    await refreshViews();
  }

  async function toggleViewShare(id: string) {
    const v = savedViews.find((x) => x.id === id);
    if (!v) return;
    await DashboardViewService.save({ ...v, shared: !v.shared });
    await refreshViews();
  }

  function applyView(v: DashboardView) {
    setConfig({ ...DEFAULT_CONFIG, ...(v.config as Partial<ViewConfig>) });
    setActiveViewId(v.id);
  }

  // ─── Render ───────────────────────────────────────────────────────

  if (loading && members.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <div
          style={{
            height: 'var(--space-7)',
            borderBottom: '1px solid var(--bd)',
            background: 'var(--sf)',
          }}
        />
        <div
          role="status"
          aria-label="Loading forecasting data"
          style={{ padding: 'var(--space-4)' }}
        >
          <div
            style={{
              width: 180,
              height: 22,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--sa)',
              animation: 'pulse 1.4s ease-in-out infinite',
              marginBottom: 'var(--space-4)',
            }}
          />
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                height: 44,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--bd)',
                background: 'var(--sf)',
                marginBottom: 'var(--space-2)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${70 - i * 8}%`,
                  background: 'var(--sa)',
                  animation: 'pulse 1.4s ease-in-out infinite',
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ─── Toolbar ─── */}
      <Toolbar
        weekStart={weekStart}
        onWeekChange={setWeekStart}
        config={config}
        onConfigChange={setConfig}
        departments={departments}
        teams={teams}
        projects={projects}
        savedViews={savedViews}
        activeViewId={activeViewId}
        onApplyView={applyView}
        onSaveView={saveCurrentAsView}
        onUpdateView={updateActiveView}
        onDeleteView={deleteView}
        onSetDefaultView={setDefaultView}
        onRenameView={renameView}
        onShareToggle={toggleViewShare}
        currentUserId={currentUserId}
        canBulk={canBulk}
        scopeOptions={scopeOptions}
        onCopyPrevWeek={copyPreviousWeek}
        copying={copying}
        viewIsDirty={viewIsDirty}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {loadError && (
        <div
          role="alert"
          style={{
            padding: '8px var(--space-4)',
            fontSize: 13,
            color: 'var(--dg)',
            background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
            borderBottom: '1px solid var(--bd)',
          }}
        >
          {loadError}
        </div>
      )}

      {/* ─── Refresh indicator (subsequent loads only — first paint uses
           the page-level loader above). Thin animated bar at the top, no
           layout shift, no blanking. */}
      {forecastsLoading && members.length > 0 && (
        <div
          aria-live="polite"
          style={{
            height: 2,
            background: 'linear-gradient(90deg, transparent, var(--ac), transparent)',
            backgroundSize: '200% 100%',
            animation: 'forecast-refresh-pulse 1.2s linear infinite',
          }}
        />
      )}
      <style>{`@keyframes forecast-refresh-pulse {
        0%   { background-position: 100% 0; }
        100% { background-position: -100% 0; }
      }`}</style>

      {/* ─── Grid ─── */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div
          style={{
            minWidth: 1024,
            display: 'grid',
            gridTemplateColumns: '260px repeat(7, 1fr) 140px',
            background: 'var(--sf)',
          }}
        >
          {/* Day header */}
          <div style={headerCell}>People</div>
          {weekDays.map((d) => (
            <DayHeaderCell
              key={d}
              day={d}
              isToday={d === todayIso()}
              showWeekend={config.showWeekend}
            />
          ))}
          <div style={{ ...headerCell, textAlign: 'right' }}>
            {config.groupBy === 'project' ? 'Week total' : 'Week / Capacity'}
          </div>

          {/* ─── Project view: projects-as-rows with breakdown sub-rows ─── */}
          {config.groupBy === 'project' ? (
            <ProjectView
              projects={visibleProjects}
              members={visibleMembers}
              forecasts={forecasts}
              weekDays={weekDays}
              breakdown={config.projectBreakdown}
              teams={teams}
              teamAssignments={teamAssignments}
              projectAssignments={projectAssignments}
              departments={departments}
              collapsedGroups={collapsedGroups}
              onToggleGroup={(id) => {
                const next = new Set(collapsedGroups);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                setCollapsedGroups(next);
              }}
              onCellClick={(memberId, dayDate, forecastId) =>
                setEditingCell({ memberId, dayDate, forecastId })
              }
              canEditCell={canEditCell}
              showWeekend={config.showWeekend}
            />
          ) : (
            // ─── People-as-rows view (Department / Flat) ───
            <>
              {groupedRows.map((g) => {
                const collapsed = collapsedGroups.has(g.id);
                return (
                  <React.Fragment key={g.id}>
                    {config.groupBy !== 'none' && (
                      <GroupHeader
                        group={g}
                        collapsed={collapsed}
                        onToggle={() => {
                          const next = new Set(collapsedGroups);
                          if (next.has(g.id)) next.delete(g.id);
                          else next.add(g.id);
                          setCollapsedGroups(next);
                        }}
                      />
                    )}
                    {!collapsed &&
                      g.members.map((m) => {
                        const cap = capacityByMember.get(m.id);
                        return (
                          <MemberRow
                            key={m.id}
                            member={m}
                            weekDays={weekDays}
                            forecastsByDay={forecastIndex.get(m.id) || new Map()}
                            projects={projects}
                            capacity={
                              cap || {
                                rawTarget: 40,
                                vacationHours: 0,
                                workedHours: 0,
                                availableHours: 40,
                                totalHours: 0,
                                isPartTime: false,
                                color: 'var(--tm)',
                                label: 'On target',
                              }
                            }
                            dailyTarget={dailyTargetFor(m)}
                            canEdit={canEditCell(m.id)}
                            onCellClick={(dayDate, forecastId) =>
                              setEditingCell({ memberId: m.id, dayDate, forecastId })
                            }
                          />
                        );
                      })}
                  </React.Fragment>
                );
              })}

              {visibleMembers.length === 0 && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <EmptyState title="No team members match the current filters." />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── Cell editor popover ─── */}
      {editingCell &&
        (() => {
          const m =
            members.find((x) => x.id === editingCell.memberId) ||
            ({ id: editingCell.memberId, name: 'Unknown', active: true } as TeamMember);
          // Assigned project set for this member: project_assignments rows
          // ∪ projects they PM/PIC ∪ projects they have any forecast on this
          // week (covers ad-hoc cells where no formal assignment exists yet).
          const assignedIds = new Set<string>();
          for (const a of projectAssignments) {
            if (a.teamMemberId === m.id) assignedIds.add(a.projectId);
          }
          for (const p of projects) {
            if (p.pmId === m.id || p.picId === m.id) assignedIds.add(p.id);
          }
          for (const f of forecasts) {
            if (f.teamMemberId === m.id && f.projectId) assignedIds.add(f.projectId);
          }
          return (
            <CellEditor
              member={m}
              dayDate={editingCell.dayDate}
              forecastId={editingCell.forecastId}
              existing={
                (forecastIndex.get(editingCell.memberId) || new Map()).get(editingCell.dayDate) ||
                []
              }
              projects={visibleProjects}
              allProjects={projects}
              assignedProjectIds={assignedIds}
              canEdit={canEditCell(editingCell.memberId)}
              onClose={() => setEditingCell(null)}
              onSaved={() => {
                setEditingCell(null);
                loadForecasts();
              }}
            />
          );
        })()}
    </div>
  );
}
